import { notification } from "antd";
import axios from "axios";
import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux";
import { stateGlobal } from "../../interfaces/Global";
import { IAlteracoes } from "../../pages/ConfiguracoesDoSistema/components/Aplicativo/ListVersaoApp/useListVersaoApp";
import { formatIconNameFontAwesome, parseIconNameToFa } from "../../services/Conversores";
import { urlIconeModulo, urlModulo } from "../../services/request/urls";
import { getToken } from "../../services/token";

interface IModulo {
    descricao: string;
    id: number;
    modo_estado: boolean;
    nome: string;
    path: string;
    status: boolean;
    uuid: string;
    icone: {
        id: number;
        uuid: number;
        nome: string;
        descricao: string;
        file: string;
    } | null;
}

interface IItemAlteracao {
    titulo: string;
    descricao: string;
    modulo?: string;
    imagem?: {
        id: number;
        uuid: number;
        nome: string;
        file: string;
    };
    icone?: {
        originalName: string;
        appName: string;
    };
}

interface IProps {
    value?: IAlteracoes[];
    onChange?: (alteracoes: IAlteracoes[]) => void;
}

const defaultIcon = {
    id: 0,
    uuid: 0,
    nome: '',
    file: ''
}

export default function useInputAppChanges({ value, onChange }: IProps) {
    const propsState = useSelector((state: stateGlobal) => state.global);
    const [visible, setVisible] = useState(false);
    const [selectedModule, setSelectedModule] = useState<string | ''>('');
    const [selectedIcon, setSelectedIcon] = useState<string | ''>('');
    const [alteracoes, setAlteracoes] = useState<Array<IItemAlteracao>>([]);
    const [moduleVisible, setModuleVisible] = useState(true);
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editingItem, setEditingItem] = useState<IItemAlteracao | null>(null);

    const [loadingData, setLoadingData] = useState(false);
    const [modulos, setModulos] = useState<IModulo[]>([]);

    useEffect(() => {
        setLoadingData(true);
        const buscaDeDados = async () => {
            try {
                let result = await axios.get(urlModulo,
                    { headers: { 'Authorization': 'Bearer ' + getToken() } });

                setModulos(result.data);
                setLoadingData(false);

            } catch (error) {
                setLoadingData(false);
                notification.error({
                    message: 'Erro',
                    description:
                        'Não foi possivel buscar os dados!',
                });
            }
        }

        buscaDeDados();

    }, []);

    useEffect(() => {
        const parsed: IAlteracoes[] = typeof value === 'string' ? JSON.parse(value) : value;

        if (!modulos || modulos.length === 0) {
            return;
        }

        if (parsed && parsed.length > 0) {
            let alteracaoFormatted: IItemAlteracao[] = [];


            parsed.map((item) => {
                const modulo = modulos.find((m) => m.icone?.nome === item.icone || m.icone?.nome === item.imagem);

                if (modulo) {
                    alteracaoFormatted.push({
                        titulo: item.titulo,
                        descricao: item.descricao,
                        modulo: modulo.nome,
                        imagem: {
                            id: modulo.icone?.id || defaultIcon.id,
                            uuid: modulo.icone?.uuid || defaultIcon.uuid,
                            nome: modulo.icone?.nome || defaultIcon.nome,
                            file: modulo.icone?.file || defaultIcon.file
                        },
                    });
                } else {
                    alteracaoFormatted.push({
                        titulo: item.titulo,
                        descricao: item.descricao,
                        icone: item.icone ? {
                            originalName: parseIconNameToFa(item.icone),
                            appName: item.icone
                        } : undefined
                    });
                }
            });

            setAlteracoes(alteracaoFormatted);
        }
    }, [value, modulos]);

    const handleCancelEdit = useCallback(() => {
        setTitulo('');
        setDescricao('');
        setSelectedModule('');
        setSelectedIcon('');
        setModuleVisible(true);
        setIsEditing(false);
        setEditingItem(null);
        setVisible(false);
    }, []);

    const handleInsertAlteracoes = useCallback(() => {
        if ((selectedModule === '' && selectedIcon === '') || !titulo) {
            notification.error({
                message: 'Erro',
                description: 'Selecione o módulo/ícone e o título da alteração'
            });
            return;
        }

        const modulo = modulos.find((item) => item.nome === selectedModule);

        let itemAlteracao: IItemAlteracao;

        if (modulo) {
            itemAlteracao = {
                titulo,
                descricao,
                modulo: modulo.nome,
                imagem: {
                    id: modulo.icone?.id || defaultIcon.id,
                    uuid: modulo?.icone?.uuid || defaultIcon.uuid,
                    nome: modulo?.icone?.nome || defaultIcon.nome,
                    file: modulo?.icone?.file || defaultIcon.file
                },
            };
        } else {
            itemAlteracao = {
                titulo,
                descricao,
                icone: {
                    originalName: selectedIcon,
                    appName: formatIconNameFontAwesome(selectedIcon)
                }
            };
        }


        setAlteracoes([...alteracoes, itemAlteracao]);
        setTitulo('');
        setDescricao('');
        setSelectedModule('');
        handleChangeModuleVisible(true);
    }, [titulo, descricao, selectedModule, selectedIcon, alteracoes]);

    const handleUpdateAlteracoes = useCallback(() => {
        if ((selectedModule === '' && selectedIcon === '') || !titulo) {
            notification.error({
                message: 'Erro',
                description: 'Selecione o módulo/ícone e o título da alteração'
            });
            return;
        }

        const modulo = modulos.find((item) => item.nome === selectedModule);

        let itemAlteracao: IItemAlteracao;

        if (modulo) {
            itemAlteracao = {
                titulo,
                descricao,
                modulo: modulo.nome,
                imagem: {
                    id: modulo.icone?.id || defaultIcon.id,
                    uuid: modulo?.icone?.uuid || defaultIcon.uuid,
                    nome: modulo?.icone?.nome || defaultIcon.nome,
                    file: modulo?.icone?.file || defaultIcon.file
                },
            };
        } else {
            itemAlteracao = {
                titulo,
                descricao,
                icone: {
                    originalName: selectedIcon,
                    appName: formatIconNameFontAwesome(selectedIcon)
                }
            };
        }

        const alteracoesAtualizadas = alteracoes.map((item) =>
            item === editingItem ? itemAlteracao : item
        );

        setAlteracoes(alteracoesAtualizadas);
        setTitulo('');
        setDescricao('');
        setSelectedModule('');
        setIsEditing(false);
        handleChangeModuleVisible(true);



    }, [titulo, descricao, selectedModule, selectedIcon, alteracoes, editingItem]);

    const handleEditAlteracoes = useCallback((alteracao: IItemAlteracao) => {
        setTitulo(alteracao.titulo);
        setDescricao(alteracao.descricao);
        handleSelectModule(alteracao.modulo);
        if (alteracao.modulo) {
            setSelectedModule(alteracao.modulo);
            setModuleVisible(true);
        }
        if (alteracao.icone) {
            setSelectedIcon(alteracao.icone.originalName);
            handleSelectIcon(alteracao.icone.originalName);
            setModuleVisible(false);
        }
        setIsEditing(true);
        const alteracoesFiltered = alteracoes.filter((item) => item === alteracao)[0];
        setEditingItem(alteracoesFiltered);
        setVisible(true);
    }, [alteracoes, selectedModule, selectedIcon]);

    const handleRemoveAlteracoes = useCallback((alteracao: IItemAlteracao) => {
        setAlteracoes(alteracoes.filter((item) => item !== alteracao));
    }, [alteracoes]);

    const handleSelectModule = (iconName: any) => {
        setSelectedModule(iconName);
    };

    const handleSelectIcon = (iconName: any) => {
        setSelectedIcon(iconName);
    };

    const handleChangeModuleVisible = (value: boolean) => {
        setModuleVisible(value);
        setTitulo('');
        setDescricao('');
        setSelectedModule('');
        setSelectedIcon('');
    }

    useEffect(() => {
        if (onChange) {
            const alteracoesFormatted = alteracoes.map((alteracao) => {
                return {
                    titulo: alteracao.titulo,
                    descricao: alteracao.descricao,
                    icone: alteracao.icone?.appName,
                    imagem: alteracao.imagem?.nome,
                    fileIcone: alteracao.icone?.originalName,
                    fileImage: alteracao.imagem?.file
                }
            });
            onChange(alteracoesFormatted);
        }
    }, [alteracoes]);


    return {
        modulos,
        loadingData,
        selectedModule,
        propsState,
        handleChangeModuleVisible,
        moduleVisible,
        handleSelectModule,
        selectedIcon,
        handleSelectIcon,
        titulo,
        setTitulo,
        descricao,
        setDescricao,
        isEditing,
        handleInsertAlteracoes,
        handleUpdateAlteracoes,
        handleEditAlteracoes,
        handleRemoveAlteracoes,
        handleCancelEdit,
        alteracoes,
        setAlteracoes
    }

}
